import { getAuthorizationHeader } from 'redux/Api/employedsApi/employedsApi';
import { AppDispatch } from './index';
import { ordersApi } from 'redux/Api/ordersApi/ordersApi';

export interface QueryParams {
  page: number;
  tab: string;
  search: string;
  type_ids: number[];
  payment_type_ids?: string[];
  worker_ids: number[];
  preset_ids: number[];
  statuses: string[];
  amount_from?: number;
  amount_to?: number;
  numbers_range?: { start: string; end: string };
  date_start_range?: { start: string; end: string };
  date_end_range?: { start: string; end: string };
  create_date_range?: { start: string; end: string };
  date_paid_range?: { start: string; end: string };
  act?: {
    number?: string;
    statuses?: string[];
    create_date_range?: { start: string; end: string };
    amount_from?: number;
    amount_to?: number;
  };
  receipts?: {
    number?: string;
    statuses?: string[];
  };
  fns_receipts_seen_status?: 0 | 1;
  per_page?: number;
}

const buildOrdersUrl = (params: QueryParams & { per_page?: number }) => {
  const {
    page = 1,
    per_page = 50,
    tab,
    create_date_range,
    date_start_range,
    date_end_range,
    date_paid_range,
    numbers_range,
    search,
    statuses,
    amount_from,
    amount_to,
    type_ids,
    payment_type_ids,
    worker_ids,
    preset_ids,
    act,
    receipts,
    fns_receipts_seen_status,
  } = params;

  const searchParams = new URLSearchParams();

  searchParams.set('page', page.toString());
  searchParams.set('per_page', per_page.toString());

  const tabParams: Record<string, string> = {
    'customer-action-required': 'customer_action_required',
    'worker-action-required': 'worker_action_required',
    finished: 'finished',
    draft: 'draft',
  };

  if (tab && tab !== 'all' && tabParams[tab]) {
    searchParams.set(tabParams[tab], '1');
  }

  if (search) {
    searchParams.set('search', search);
  }

  if (create_date_range) {
    searchParams.set('create_date_range[start]', create_date_range.start);
    searchParams.set('create_date_range[end]', create_date_range.end);
  }

  if (date_start_range) {
    searchParams.set('date_start_range[start]', date_start_range.start);
    searchParams.set('date_start_range[end]', date_start_range.end);
  }

  if (date_end_range) {
    searchParams.set('date_end_range[start]', date_end_range.start);
    searchParams.set('date_end_range[end]', date_end_range.end);
  }

  if (date_paid_range) {
    searchParams.set('date_paid_range[start]', date_paid_range.start);
    searchParams.set('date_paid_range[end]', date_paid_range.end);
  }

  if (numbers_range) {
    searchParams.set('numbers_range[start]', numbers_range.start ?? '');
    searchParams.set('numbers_range[end]', numbers_range.end ?? '');
  }

  if (statuses && statuses.length > 0) {
    statuses.forEach((status, index) => {
      searchParams.set(`statuses[${index}]`, status);
    });
  }

  if (amount_from !== undefined) {
    searchParams.set('amount_from', amount_from.toString());
  }

  if (amount_to !== undefined) {
    searchParams.set('amount_to', amount_to.toString());
  }

  if (type_ids && type_ids.length > 0) {
    type_ids.forEach((id, index) => {
      searchParams.set(`type_ids[${index}]`, id.toString());
    });
  }

  if (payment_type_ids && payment_type_ids.length > 0) {
    payment_type_ids.forEach((id, index) => {
      searchParams.set(`payment_type_ids[${index}]`, id.toString());
    });
  }

  if (worker_ids && worker_ids.length > 0) {
    worker_ids.forEach((id, index) => {
      searchParams.set(`worker_ids[${index}]`, id.toString());
    });
  }

  if (preset_ids && preset_ids.length > 0) {
    preset_ids.forEach((id, index) => {
      searchParams.set(`preset_ids[${index}]`, id.toString());
    });
  }

  if (act) {
    if (act.number) {
      searchParams.set('act[number]', act.number);
    }
    if (act.statuses && act.statuses.length > 0) {
      act.statuses.forEach((status, index) => {
        searchParams.set(`act[statuses][${index}]`, status);
      });
    }
    if (act.create_date_range) {
      searchParams.set('act[create_date_range][start]', act.create_date_range.start);
      searchParams.set('act[create_date_range][end]', act.create_date_range.end);
    }
    if (act.amount_from !== undefined) {
      searchParams.set('act[amount_from]', act.amount_from.toString());
    }
    if (act.amount_to !== undefined) {
      searchParams.set('act[amount_to]', act.amount_to.toString());
    }
  }

  if (receipts) {
    if (receipts.number !== undefined) {
      searchParams.set('receipts[number]', receipts.number);
    }
    if (receipts.statuses && receipts.statuses.length > 0) {
      receipts.statuses.forEach((status, index) => {
        searchParams.set(`receipts[statuses][${index}]`, status);
      });
    }
  }

  if (fns_receipts_seen_status !== undefined && fns_receipts_seen_status !== null) {
    searchParams.set('fns_receipts_seen_status', fns_receipts_seen_status.toString());
  }

  return `${process.env.REACT_APP_API_URL}/api/v2/orders?${searchParams.toString()}`;
};

export const handleOrderChanged = async (
  queryParams: QueryParams,
  dispatch: AppDispatch,
) => {
  try {
    const lastPage = queryParams.page || 1;
    let allOrdersData: any[] = [];
    let lastResponseMeta: any = null;

    for (let page = 1; page <= lastPage; page++) {
      const pageParams = { ...queryParams, page };
      const url = buildOrdersUrl(pageParams);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      });

      if (!response.ok) {
        console.error(`Ошибка при получении заказов на странице ${page}`);
        continue;
      }

      const responseData = await response.json();
      const pageData = responseData.data || [];

      if (pageData.length > 0) {
        allOrdersData = [...allOrdersData, ...pageData];
      }

      if (page === lastPage) {
        lastResponseMeta = responseData.meta;
      }
    }

    const ordersQueryParams = {
      ...queryParams,
      receipts: queryParams.receipts
        ? {
            ...queryParams.receipts,
            number: queryParams.receipts.number
              ? String(queryParams.receipts.number)
              : undefined,
          }
        : undefined,
    };

    dispatch(
      ordersApi.util.updateQueryData('getOrders', ordersQueryParams, (draft) => {
        draft.data = allOrdersData;
        draft.meta.total = lastResponseMeta?.total ?? draft.meta.total;
        draft.meta.current_page = queryParams.page || 1;
        draft.meta.last_page = lastResponseMeta?.last_page || 1;
        draft.meta.per_page = lastResponseMeta?.per_page || 50;
      }) as any,
    );
  } catch (error) {
    console.error('Ошибка при обработке изменения заказа:', error);
  }
};
